/*
 * WaWiKundenKreditanspannung.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-26 in huWaWi3 33.11.2
 * Copyright (c) 2023 HUDORA GmbH
 */

import { LinkRender } from '../lib/hd-link'
import { LadeChecker } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import { Typography } from '@mui/material'
import { assertIsArray, assertIsDefined, assertIsNumber, assertIsObject } from 'assertate-debug'
import gql from 'graphql-tag'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'urql'

import { HwKunde } from '../gql/graphql'
import { VERBAND_FRAGMENT } from '../Stammdaten/StammVerbaendeApp'

export function WaWiKundenKreditanspannung({ kunde }: { kunde: HwKunde }) {
  const [{ fetching, data, error }] = useQuery({ query: VERBAND_QUERY, variables: { nr: kunde.verbandsnr } })
  const [rechnungenOffen, setRechnungenOffen] = useState(-1)
  const [zuteilbareAuftrage, setZuteilbareAuftrage] = useState(-1)

  return (
    <div>
      <Typography component="h2" variant="h4" gutterBottom>
        Kreditanspannung
      </Typography>

      {rechnungenOffen !== -1 && zuteilbareAuftrage !== -1 ? (
        <div>
          <Typography component="p" gutterBottom>
            Aktuell hat der Kunde <ValueRender value={zuteilbareAuftrage} format={'eurocent'} /> an Aufträgen
            in der Komissionierung und <ValueRender value={rechnungenOffen} format={'eurocent'} /> an offenen
            Rechnungen. Das bedeutet eine{' '}
            <b>
              Kreditanspannung von{' '}
              <ValueRender value={zuteilbareAuftrage + rechnungenOffen} format={'eurocent'} />
            </b>
            . <br />
            Der Kunde hat ein Kreditlimit von <ValueRender
              value={kunde.kreditlimit}
              format={'eurocent'}
            />.{' '}
            {kunde.kreditlimit === 0 ? (
              <>
                Ein Kreditlimit von 0 bedeutet, dass der Kunde <b>unbegrenztes Kreditlimit</b> hat.
              </>
            ) : null}
            {kunde.kreditlimit === 1 ? (
              <>
                Ein Kreditlimit von 1 Cent bedeutet, dass der Alle Aufträge des Kunden durch die Buchhaltung
                freigegeben werden müssen ("<b>Vorauskasse</b>).
              </>
            ) : null}
            {kunde.kreditlimit > 1 ? (
              <>
                Das bedeutet, es können heute noch Aufträge für{' '}
                <b>
                  <ValueRender
                    value={kunde.kreditlimit - zuteilbareAuftrage - rechnungenOffen}
                    format={'eurocent'}
                  />
                </b>{' '}
                in die Zuteilung gehen.
              </>
            ) : null}
          </Typography>
          <LadeChecker loading={fetching} error={error} data={data}>
            <Typography component="p" gutterBottom>
              Der Verband {data.entity.name} <LinkRender value={kunde.verbandsnr} /> hat ein Kreditlimit von{' '}
              <b>
                <ValueRender value={data.entity.kreditlimit} format={'eurocent'} />
              </b>
              .{' '}
              {data.entity.kreditlimit === 0 ? (
                <>
                  Ein Kreditlimit von 0 bedeutet, dass der Verband <b>unbegrenztes Kreditlimit</b> hat.
                </>
              ) : null}
              {data.entity.kreditlimit === 1 ? (
                <>
                  Ein Kreditlimit von 1 Cent bedeutet, dass der Alle Aufträge des Verbandes durch die
                  Buchhaltung freigegeben werden müssen ("<b>Vorauskasse</b>").
                </>
              ) : null}
              {data.entity.kreditlimit > 1 ? (
                <>
                  Das bedeutet, es können heute noch Aufträge für{' '}
                  <b>
                    <ValueRender
                      value={data.entity.kreditlimit - zuteilbareAuftrage - rechnungenOffen}
                      format={'eurocent'}
                    />
                  </b>{' '}
                  in die Zuteilung gehen.
                  <br />
                  Korrekterweise müssen dabei nicht nur die offenen Aufträge und Rechnungen für den Kunden{' '}
                  {kunde.designator} berücksichtigt werden, sondern auch die für alle anderen
                  Verbandsmitgliedern von <LinkRender value={kunde.verbandsnr} />. Das ist aktuell noch nicht
                  der Fall.
                </>
              ) : null}
            </Typography>
          </LadeChecker>
          <Typography component="p" gutterBottom>
            Aufträge werden nur zugeteilt, wenn sowohl der Kunde als auch der Verband noch Kreditlimit zur
            Verfügung haben.
          </Typography>
        </div>
      ) : null}
      <AuftragswertKunde kundenNr={kunde.designator} setZuteilbareAuftrage={setZuteilbareAuftrage} />
      <RechnungswertKunde kunde={kunde} setRechnungenOffen={setRechnungenOffen} />
    </div>
  )
}

/*
 * Code portiert von kundeKreditanspannung.ts
 *
 * Created by Dr. Maximillian Dornseif 2023-11-26 in @hudora/huwawi-cli 1.4.0
 * Copyright (c) 2023 HUDORA GmbH
 */

function AuftragswertKunde({
  kundenNr,
  setZuteilbareAuftrage,
}: {
  kundenNr: string
  setZuteilbareAuftrage: (x: number) => void
}) {
  const [{ fetching, data, error }] = useQuery({ query: AUFTRAGSWERT_QUERY, variables: { kundenNr } })
  const title = (
    <Typography component="h3" variant="h5" gutterBottom sx={{ mt: '2em' }}>
      Offene Aufträge
    </Typography>
  )
  if (fetching) {
    return (
      <>
        {title}
        <LadeChecker loading={fetching} error={error} />
      </>
    )
  }

  const aggAlleAuftrage: Record<string, Record<string, number>> = {}
  const aggZuteilbareAuftrage: Record<string, Record<string, number>> = {}
  const auftraege = data?.hwKunde?.auftraege
  assertIsArray(auftraege)
  // debug(util.inspect(auftraege, {depth: null, colors: true}))
  // debug('Auftragswertermittlung abgefragt', auftraege?.length)

  for (const auftrag of auftraege || []) {
    assertIsDefined(auftrag)
    assertIsObject(auftrag.wertBerechnung)
    for (const art of ['bestellt', 'offen', 'geliefert']) {
      aggAlleAuftrage[art] = aggAlleAuftrage[art] || {}
      aggZuteilbareAuftrage[art] = aggZuteilbareAuftrage[art] || {}
      for (const typ of [
        'warenwert',
        'fh',
        'kundenPreis',
        'summe_netto_betraege',
        'netto_betrag',
        'steuer_betrag',
        'brutto_betrag',
        'abschlag_betrag',
      ]) {
        // @ts-expect-error: TS7053 weil wir hier schlechte typings haben
        const wert = (auftrag.wertBerechnung?.[art] as Record<string, number>)?.[typ]
        if (wert) {
          aggAlleAuftrage[art][typ] = aggAlleAuftrage[art][typ] || 0
          aggAlleAuftrage[art][typ] += wert
          aggZuteilbareAuftrage[art][typ] = aggZuteilbareAuftrage[art][typ] || 0

          if ((auftrag?.status || '300') >= '300') {
            aggZuteilbareAuftrage[art][typ] += wert
          }
        } else {
          console.error('Kein Wert für', auftrag)
        }
      }
    }
  }

  // debug(util.inspect(aggAlleAuftrage, {depth: null, colors: true}))
  // debug('Auftragswertermittlung berechnet')

  setZuteilbareAuftrage(aggZuteilbareAuftrage.offen.netto_betrag)

  return (
    <>
      {title}
      <Typography component="p">
        Der Kunde hat {auftraege.length} offene Aufträge mit einem geschätzten Netto-Rechnungsbetrag von{' '}
        <ValueRender value={aggAlleAuftrage.offen.netto_betrag} format={'eurocent'} />.<br />
        Davon werden aber nur Aufträge im Wert von{' '}
        <b>
          <ValueRender value={aggZuteilbareAuftrage.offen.netto_betrag} format={'eurocent'} />
        </b>{' '}
        zur Kreditlimitberechnung herangezogen, weil nur sie auch tatsächlich ausgeliefert werden können
        (Status 300 oder höher).
      </Typography>
      <ul>
        {auftraege.map((auftrag) => {
          return (
            <li key={auftrag.auftragsnr}>
              <Typography color={(auftrag?.status || '300') >= '300' ? 'text.primary' : 'text.secondary'}>
                <LinkRender value={auftrag.auftragsnr} /> {auftrag.status}{' '}
                <ValueRender value={auftrag.wertBerechnung.offen.netto_betrag} format={'eurocent'} />
              </Typography>
            </li>
          )
        })}
      </ul>
    </>
  )
}
const AUFTRAGSWERT_QUERY = gql`
  query GuiKundeKreditanspannung($kundenNr: ID!) {
    hwKunde(designator: $kundenNr) {
      id

      kreditlimit

      auftraege(filter: { erledigt: { eq: false } }) {
        id
        auftragsnr
        status
        wertBerechnung {
          bestellt {
            warenwert
            fh
            kundenPreis
            summe_netto_betraege
            netto_betrag
            steuer_betrag
            brutto_betrag
            abschlag_betrag
          }
          offen {
            warenwert
            fh
            kundenPreis
            summe_netto_betraege
            netto_betrag
            steuer_betrag
            brutto_betrag
            abschlag_betrag
          }
          geliefert {
            warenwert
            fh
            kundenPreis
            summe_netto_betraege
            netto_betrag
            steuer_betrag
            brutto_betrag
            abschlag_betrag
          }
        }
      }
    }
  }
`
function RechnungswertKunde({
  kunde,
  setRechnungenOffen,
}: {
  kunde: HwKunde
  belegart: string
  setRechnungenOffen: (x: number) => void
}) {
  const [cutOff, setCutOff] = useState('')
  useEffect(() => {
    setCutOff(new Date(Date.now() - kunde.nettotage * 24 * 60 * 60 * 1000).toISOString())
  }, [kunde.nettotage])
  const [{ fetching, data, error }] = useQuery({
    query: BELEG_QUERY,
    variables: { kundenNr: kunde?.designator, belegart: 'Rechnung', cutOff },
    pause: cutOff === '',
  })
  const [{ fetching: fetchingSammel, data: dataSammel, error: errorSammel }] = useQuery({
    query: BELEG_QUERY,
    variables: { kundenNr: kunde?.designator, belegart: 'Sammelrechnung', cutOff },
    pause: cutOff === '',
  })

  const title = (
    <>
      <Typography component="h3" variant="h5" gutterBottom sx={{ mt: '2em' }}>
        Offene Rechnungen
      </Typography>
      <Typography component="p">
        Da wir ja in der Warenwirtschaft nicht wissen, ob eine Rechnung schon bezahlt wurde, gehen wir davon
        aus, dass alle Rechnungen mit Zahlungsziel noch offen sind und alle nach ihrem Zahlungsziel schon
        bezahlt wurden.
        <br />
        Das bedeutet bei dem Kunden Zahlungsziel von {kunde.nettotage} Tagen, dass wir hier nur die Rechnungen
        nach dem {cutOff} einbeziehen.
        <br />
        Sollte der Kunde früher - mit Skonto - oder später - mit Verzug - bezahlt haben, so wird das hier
        nicht beachtet. Dann müssen im ersten Fall die Aufträge manuell freigegeben werden; im zweiten Fall
        das Kreditlimit manuell verringert werden.
      </Typography>
    </>
  )
  if (fetching) {
    return (
      <>
        {title}
        <LadeChecker loading={fetching} error={error} data={data} objectName={'Rechnungen'} />
        <LadeChecker
          loading={fetchingSammel}
          error={errorSammel}
          data={dataSammel}
          objectName={'Sammelrechnungen'}
        />
      </>
    )
  }

  const rechnungsBrutto =
    data?.hwKunde?.belege.reduce((a, b) => a + b.faktura?.brutto_betrag, 0) +
    dataSammel?.hwKunde?.belege.reduce((a, b) => a + b.faktura?.brutto_betrag, 0)
  const rechnungsNetto = Math.round(rechnungsBrutto / 1.19)
  setRechnungenOffen(rechnungsNetto)

  return (
    <>
      {title}
      <Typography component="p">
        Der Kunde hat {data?.hwKunde?.belege.length + dataSammel?.hwKunde?.belege.length} (vermutlich) offene
        Rechnungen mit einem Netto-Rechnungsbetrag von{' '}
        <b>
          <ValueRender value={rechnungsNetto} format={'eurocent'} />
        </b>
        .
      </Typography>
      <Typography component="p" color={'text.secondary'}>
        Diese Berechnung basiert aus technischen Gründen an dieser Stelle auf Brutto-Beträgen (
        <ValueRender value={rechnungsBrutto} format={'eurocent'} />) von denen pauschal 19% Steuer abgezogen
        wird.
      </Typography>
      <ul>
        {data?.hwKunde?.belege.map((beleg) => {
          return (
            <li key={beleg.id}>
              <Typography>
                <LinkRender value={beleg.designator} />{' '}
                <Typography component="span" color={'text.secondary'}>
                  <ValueRender value={beleg.belegdatum} format="date" />
                </Typography>{' '}
                <ValueRender value={beleg.faktura.brutto_betrag / 1.19} format={'eurocent'} />
              </Typography>
            </li>
          )
        })}
        {dataSammel?.hwKunde?.belege.map((beleg) => {
          return (
            <li key={beleg.id}>
              <Typography>
                <LinkRender value={beleg.designator} />{' '}
                <Typography component="span" color={'text.secondary'}>
                  <ValueRender value={beleg.belegdatum} format="date" />
                </Typography>{' '}
                <ValueRender value={beleg.faktura.brutto_betrag / 1.19} format={'eurocent'} />
              </Typography>
            </li>
          )
        })}
      </ul>
    </>
  )
}
const BELEG_QUERY = gql`
  query GuiOffeneRechnungen($kundenNr: ID!, $cutOff: DateTime!, $belegart: String!) {
    hwKunde(designator: $kundenNr) {
      id
      nettotage
      kreditlimit
      verband {
        kreditlimit
      }
      belege(filter: { belegart: { eq: $belegart }, belegdatum: { gt: $cutOff } }) {
        id
        designator
        belegart
        belegdatum
        empfaenger
        faktura {
          brutto_betrag
        }
        gruppennr
        kundennr
        name
      }
    }
  }
`
const VERBAND_QUERY = gql`
  query EinVerbandQuery($nr: ID!) {
    entity: hwKundenVerband(designator: $nr) {
      ...verbandFragment
    }
  }
  ${VERBAND_FRAGMENT}
`
